/**
 * Which file extensions the editor treats as a **scene** rather than a single
 * asset (CONTENT_MODEL_SPEC v4 §4).
 *
 * `.glz` is the bundle, and the only archive the editor writes. `.tracex` and
 * `.glxf` stay on every accept list as **read-only inputs** — nothing emits
 * them any more, and they come off when the gated reader retirement lands
 * (trace-viewer#816). `.trace` and `.zip` are the legacy readers, `.aero` a
 * legacy catalog row format that never had a reader constant.
 *
 * The viewer's own `SCENE_EXTENSIONS` predates `.glz`, so this adds it rather
 * than restating the list: when the viewer's set gains `.glz` this module
 * keeps answering the same thing, and when `.tracex`/`.glxf` leave the viewer's
 * set they leave here too.
 */

import { SCENE_EXTENSIONS, toDotExtensions } from 'trace-viewer/scene';

const sceneExtensionSet = new Set([...SCENE_EXTENSIONS, 'glz']);

/** Scene-format extensions, without the leading dot, lowercase. */
export const EDITOR_SCENE_EXTENSIONS: ReadonlySet<string> = sceneExtensionSet;

/** The same set in `.ext` form, for `<input accept>` and picker filters. */
export const EDITOR_SCENE_DOT_EXTENSIONS: readonly string[] = toDotExtensions(sceneExtensionSet);

/**
 * The extension the editor writes an archive under, dot included.
 *
 * There is exactly one: a bundle download is `.glz` and nothing else. `.tracex`
 * is read-only (trace-viewer#820 removed the write path), so this constant has
 * no sibling by design — a second one would be a second archive format.
 */
export const BUNDLE_EXTENSION = '.glz';

/** The download name for a scene or a subtree exported as a bundle. */
export function bundleFileName(name: string | null | undefined): string {
    return `${name || 'scene'}${BUNDLE_EXTENSION}`;
}

/** The extension of a file name, lowercase and without the dot (`''` if none). */
export function fileExtension(fileName: string): string {
    return fileName.toLowerCase().match(/\.([^./\\]+)$/)?.[1] ?? '';
}

/**
 * Whether a file name names a scene, so it is placed as objects rather than
 * imported as one asset. `.aero` is included here (and only here) because a
 * legacy catalog row can still carry it.
 */
export function isSceneFileName(fileName: string): boolean {
    const ext = fileExtension(fileName);
    return EDITOR_SCENE_EXTENSIONS.has(ext) || ext === 'aero';
}
